import React, { useCallback, useState } from 'react';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { ConsolidatedLocationView, EventPanelFormType } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { VStack, FormRow } from '@cad-arc';
import { useLocation } from 'react-router-dom';
import { ClassificationStrategy } from '../common/types';
import { FormInput } from '../forms/integrated-form-controls/form-input';
import { FormLocationSelect } from '../forms/integrated-form-controls/form-location-select';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { FrEventLocationManualGpsPrefillContent } from './fr-event-location-manual-gps-prefill-content';
import { FrEventLocationAutomaticGpsPrefillContent } from './fr-event-location-automatic-gps-prefill-content';
import { FR_CREATE_EVENT_SUB_PATH, FR_EVENT_ROOT_PATH } from '../router/constants';
import { useUnitLocationStore } from '../fr-unit-location/use-unit-location-store';
import {
    useCreateEventFormPrefill,
    useHandleReverseGeocodeClosestLocation,
    usePrefillLocationInfo,
} from './hooks';
import { isValidLatLng } from '../location/helpers';
import { useMaritimeFormat } from '../common/hooks/use-maritime-format';
import { MaritimeFormatsButtonsSection } from '../common/components/maritime-formats-buttons-section';

export const FrCreateOrEditEventLocationField = observer(
    function FrCreateOrEditEventLocationField() {
        const location = useLocation();
        const { form, isSubmitting } = useIntegratedForm<EventPanelFormType>();
        const prefillLocationInfo = usePrefillLocationInfo();
        const [
            userManuallyChangedCreateEventLocation,
            setUserManuallyChangedCreateEventLocation,
        ] = useState(false);
        const [locationCoordinates, setLocationCoordinates] = useState<{
            latitude?: number;
            longitude?: number;
        }>(() => {
            const locations = form.getValues('location') as ConsolidatedLocationView;

            return {
                latitude: locations?.latitude,
                longitude: locations?.longitude,
            };
        });
        const { lat: unitGpsLatitude, lng: unitGpsLongitude } =
            useUnitLocationStore()?.unitGpsInfo || {};
        const { eventInitiatedFromMap } = useCreateEventFormPrefill();
        const showAutomaticGpsPrefillContent =
            location.pathname === `${FR_EVENT_ROOT_PATH}/${FR_CREATE_EVENT_SUB_PATH}` &&
            !userManuallyChangedCreateEventLocation &&
            !eventInitiatedFromMap;
        const reverseGeocodeClosestLocationUtils = useHandleReverseGeocodeClosestLocation({
            isAutomaticPrefill: showAutomaticGpsPrefillContent,
        });

        const {
            multiAgencySubdivisions: multiAgencySubdivisionsEnabled,
            subpremiseSupport: subpremiseSupportEnabled,
            frSelfInitiateGpsLocation: frSelfInitiateGpsLocationEnabled,
            maritimeCoordinatesEnabled,
        } = useEnabledFeatures();

        const { formattedValue, maritimeFormat, setMaritimeFormat } = useMaritimeFormat({
            latitude: locationCoordinates?.latitude,
            longitude: locationCoordinates?.longitude,
        });
        const shouldAutofocus =
            location.pathname === `${FR_EVENT_ROOT_PATH}/${FR_CREATE_EVENT_SUB_PATH}` &&
            !unitGpsLatitude &&
            !unitGpsLongitude;

        const handleChange = useCallback(
            (newLocation: ConsolidatedLocationView) => {
                if (frSelfInitiateGpsLocationEnabled && showAutomaticGpsPrefillContent) {
                    setUserManuallyChangedCreateEventLocation(true);
                }

                prefillLocationInfo(newLocation);
                reverseGeocodeClosestLocationUtils?.setIsPrefilledWithUnitGpsLocation(false);
                if (maritimeCoordinatesEnabled) {
                    setLocationCoordinates({
                        latitude: newLocation?.latitude,
                        longitude: newLocation?.longitude,
                    });
                }
            },
            [
                reverseGeocodeClosestLocationUtils,
                prefillLocationInfo,
                showAutomaticGpsPrefillContent,
                frSelfInitiateGpsLocationEnabled,
                maritimeCoordinatesEnabled,
                setLocationCoordinates,
            ]
        );

        return (
            <VStack width="100%" margin={0} padding={0} alignItems="flex-start">
                <FormRow>
                    <FormLocationSelect
                        control={form.control}
                        label="Location"
                        name="location"
                        rules={{ required: 'Required' }}
                        length={subpremiseSupportEnabled ? undefined : 'lg'}
                        showRequiredIndicator
                        placeInputOutside={subpremiseSupportEnabled}
                        eventNotificationName="EVENT_LOCATION"
                        mode={isSubmitting ? 'read-only' : undefined}
                        autoFocus={shouldAutofocus}
                        onChange={(newLocation) =>
                            handleChange((newLocation as unknown) as ConsolidatedLocationView)
                        }
                        classificationStrategy={
                            multiAgencySubdivisionsEnabled
                                ? ClassificationStrategy.MULTI_AGENCY
                                : ClassificationStrategy.SINGLE_AGENCY
                        }
                    />
                    {!subpremiseSupportEnabled && (
                        <FormInput
                            control={form.control}
                            label="Apt, Unit"
                            length="sm"
                            name="locationSubPremise"
                            eventNotificationName="EVENT_LOCATION_SUB_PREMISE"
                            asEditable
                        />
                    )}
                </FormRow>
                {maritimeCoordinatesEnabled &&
                    isValidLatLng(
                        locationCoordinates?.latitude,
                        locationCoordinates?.longitude
                    ) && (
                        <MaritimeFormatsButtonsSection
                            formattedValue={formattedValue}
                            activeFormat={maritimeFormat}
                            setActiveFormat={setMaritimeFormat}
                        />
                    )}
                {frSelfInitiateGpsLocationEnabled && (
                    <>
                        {showAutomaticGpsPrefillContent ? (
                            <FrEventLocationAutomaticGpsPrefillContent
                                {...reverseGeocodeClosestLocationUtils}
                            />
                        ) : (
                            <FrEventLocationManualGpsPrefillContent
                                {...reverseGeocodeClosestLocationUtils}
                            />
                        )}
                    </>
                )}
            </VStack>
        );
    }
);
